import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { poliService, tenantApi } from "../../services/api";

const fadeUp = {
    hidden: { opacity: 0, y: 28 },
    visible: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.6,
            delay: i * 0.07,
            ease: [0.25, 0.46, 0.45, 0.94],
        },
    }),
};

function CareLinePolyclinics({ slug }) {
    const [polyclinics, setPolyclinics] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        fetchPolyclinics();
    }, [slug]);

    const fetchPolyclinics = async () => {
        try {
            const response = slug
                ? await tenantApi(slug).poli.getAll()
                : await poliService.getAll();
            if (response.data.success) setPolyclinics(response.data.data);
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const getIconPath = (poli) => {
        if (
            poli.icon &&
            (poli.icon.startsWith("http") || poli.icon.startsWith("/"))
        )
            return poli.icon;
        const name = (poli.nama_poli || "").toLowerCase();
        if (name.includes("gigi")) return "/Icons/polyclinic2-careline.svg";
        if (name.includes("anak") || name.includes("ibu"))
            return "/Icons/polyclinic3-careline.svg";
        if (name.includes("dalam") || name.includes("jantung"))
            return "/Icons/polyclinic4-careline.svg";
        return "/Icons/polyclinic1-careline.svg";
    };

    return (
        <section
            id="layanan"
            className="relative overflow-hidden py-16 md:py-20 font-[Plus_Jakarta_Sans,sans-serif]"
        >
            <div className="absolute inset-0 bg-gradient-to-br from-white via-cyan-50/30 to-white pointer-events-none" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_50%,rgba(6,182,212,0.06)_0%,transparent_70%)] pointer-events-none" />

            <div className="relative z-10 mx-auto max-w-[1280px] px-4 sm:px-6 md:px-10 lg:px-16 xl:px-[60px]">
                <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
                    <motion.div
                        className="flex-shrink-0 w-full lg:w-[260px] text-center lg:text-left"
                        initial={{ opacity: 0, x: -32 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{
                            duration: 0.7,
                            ease: [0.25, 0.46, 0.45, 0.94],
                        }}
                    >
                        <div className="inline-flex items-center gap-2 bg-cyan-50 border border-cyan-200/70 text-cyan-600 text-[11px] font-bold px-3.5 py-1.5 rounded-full uppercase tracking-[0.08em] mb-5">
                            <span className="w-1.5 h-1.5 bg-cyan-500 rounded-full" />
                            Poliklinik Kami
                        </div>
                        <h2 className="text-[clamp(24px,3.5vw,40px)] font-extrabold text-gray-900 leading-tight tracking-[-0.02em] mb-4">
                            Daftar{" "}
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#06B6D4] to-[#0891B2]">
                                Poliklinik
                            </span>{" "}
                            yang Tersedia
                        </h2>
                        <p className="text-gray-400 text-[15px] max-w-[220px] mx-auto lg:mx-0 leading-relaxed font-medium">
                            Temukan layanan kesehatan yang sesuai dengan
                            kebutuhan Anda.
                        </p>
                    </motion.div>

                    <motion.div
                        className="flex-1 min-w-0 w-full"
                        initial={{ opacity: 0, x: 32 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{
                            duration: 0.7,
                            delay: 0.1,
                            ease: [0.25, 0.46, 0.45, 0.94],
                        }}
                    >
                        {isLoading ? (
                            <div className="flex flex-col gap-3">
                                {[...Array(4)].map((_, i) => (
                                    <div
                                        key={i}
                                        className="bg-white rounded-2xl px-5 py-4 border border-[#e0f2f7] animate-pulse flex items-center gap-4"
                                    >
                                        <div className="w-10 h-10 rounded-xl bg-gray-100 flex-shrink-0" />
                                        <div className="flex-1">
                                            <div className="h-3.5 bg-gray-100 rounded-full w-2/5 mb-2" />
                                            <div className="h-3 bg-gray-100 rounded-full w-4/5" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : polyclinics.length > 0 ? (
                            <>
                                <div
                                    className="flex flex-col gap-2.5 overflow-y-auto pr-1"
                                    style={{
                                        maxHeight: "320px",
                                        scrollbarWidth: "thin",
                                        scrollbarColor: "#b2e8f2 transparent",
                                    }}
                                >
                                    {polyclinics.map((poli, idx) => (
                                        <motion.div
                                            key={poli.id_poli}
                                            custom={idx}
                                            variants={fadeUp}
                                            initial="hidden"
                                            whileInView="visible"
                                            viewport={{ once: true }}
                                            whileHover={{ x: 4 }}
                                            className="group flex items-start gap-4 bg-white rounded-2xl px-5 py-4 border border-[#e0f2f7] transition-all duration-300 hover:border-cyan-400/40 hover:shadow-[0_4px_16px_rgba(6,182,212,0.12)]"
                                        >
                                            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-[#06B6D4] to-[#0891B2] transition-transform duration-300 group-hover:scale-105">
                                                <img
                                                    src={getIconPath(poli)}
                                                    alt={poli.nama_poli}
                                                    className="w-5 h-5 brightness-0 invert"
                                                />
                                            </div>
                                            <div className="flex-1 min-w-0 pt-0.5">
                                                <h3 className="text-[14px] font-bold text-gray-900 mb-1 leading-snug tracking-[-0.01em]">
                                                    {poli.nama_poli}
                                                </h3>
                                                <p className="text-gray-400 text-[12px] leading-relaxed font-medium line-clamp-2">
                                                    {poli.deskripsi ||
                                                        "Layanan kesehatan berkualitas untuk Anda."}
                                                </p>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                                {polyclinics.length > 4 && (
                                    <p className="text-center text-[11px] text-gray-300 font-semibold tracking-wide mt-2.5 uppercase">
                                        ↓ Geser untuk lihat lebih banyak
                                    </p>
                                )}
                            </>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-16 text-center">
                                <p className="text-gray-400 text-[16px] font-medium">
                                    Belum ada poliklinik tersedia.
                                </p>
                            </div>
                        )}
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

export default CareLinePolyclinics;